import { IWardrobeItem, IWardrobeItemEdit } from 'models/wardrobe/wardrobeitem';
import { IManageWardrobeItem, IManageWardrobeItemAction } from 'models/wardrobe/managewardrobeitem';
import { IStore } from 'redux/IStore';
import * as i18n from 'i18next';
import { actions as notifActions } from 'redux-notifications';
import { IImageData, ICategoryDomainValue, ICropImageObject } from 'models/common';
import { change } from 'redux-form';
const { notifSend } = notifActions;

/** Modal Module actions */
import { closeModal } from 'modules/modal';
import { SearchUsage } from 'models/enums/searchusage';
import { getWardrobeItemsSearchResults } from 'redux/modules/search/wardrobeitemsearchresults';

/** Action Types */
export const REQUEST_START: string = 'wardrobeitem/REQUEST_START';
export const REQUEST_FAILURE: string = 'wardrobeitem/REQUEST_FAILURE';
export const MANAGE_ITEM_SUCCESS: string = 'wardrobeitem/MANAGE_ITEM_SUCCESS';
export const GET_WARDROBE_ITEM_SUCCESS: string = 'wardrobeitem/GET_WARDROBE_ITEM_SUCCESS';
export const SET_IS_EDIT_MODE: string = 'wardrobeitem/SET_IS_EDIT_MODE';
export const CLEAR_FORM_DATA: string = 'wardrobeitem/CLEAR_FORM_DATA';
export const CLEAR_FORM_ERROR: string = 'wardrobeitem/CLEAR_FORM_ERROR';

/** Initial State */
const initialState: IManageWardrobeItem = {
	isFetching: false,
	categories: [],
	wardrobeItem: {
		published: false,
	},
	isEditMode: false,
};

/** Reducer */
export function manageWardrobeItemReducer(state = initialState, action: IManageWardrobeItemAction) {
	switch (action.type) {
		case REQUEST_START:
			return Object.assign({}, state, {
				isFetching: true,
				error: false,
			});

		case MANAGE_ITEM_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				wardrobeItem: action.payload.wardrobeItem,
			});

		case REQUEST_FAILURE:
			return Object.assign({}, state, {
				isFetching: false,
				message: action.payload.message,
				error: true,
			});

		case GET_WARDROBE_ITEM_SUCCESS:
			return Object.assign({}, state, {
				isFetching: false,
				wardrobeItem: action.payload.wardrobeItem,
			});

		case CLEAR_FORM_DATA:
			return Object.assign({}, state, {
				wardrobeItem: null,
			});

		case CLEAR_FORM_ERROR:
			return Object.assign({}, state, {
				isFetching: false,
				message: null,
				error: false,
			});

		case SET_IS_EDIT_MODE:
			return Object.assign({}, state, {
				isEditMode: action.payload.isEditMode,
			});

		default:
			return state;
	}
}

/** Async Action Creator */
export function getWardrobeItem(id: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/WardrobeItem/' + id + '/edit',
			Object.assign({}, getState().fetchDefault.requestInit),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((res: IWardrobeItemEdit) => {
						const wardrobeItem: IWardrobeItem = res;
						wardrobeItem.categoryKey = res.attributes.CategoryKey;
						wardrobeItem.colorKey = res.attributes.ColorKey;
						wardrobeItem.materialKey = res.attributes.MaterialKey;
						wardrobeItem.printKey = res.attributes.PrintKey;
						wardrobeItem.sizeKey = res.attributes.SizeKey;
						dispatch(getWardrobeItemSuccess(wardrobeItem));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator - create */
export function submitCreateWardrobeItem(item: IWardrobeItem) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		item.metatagTitle = item.name;
		item.metatagKeywords = item.name;
		item.metatagDescription = item.description;
		item.userId = getState().userPreferences.id;

		const newWardrobeItem: IWardrobeItemEdit = item;
		newWardrobeItem.attributes = {
			ColorKey: item.colorKey,
			MaterialKey: item.materialKey,
			PrintKey: item.printKey,
			SizeKey: item.sizeKey,
			CategoryKey: item.categoryKey,
		};

		return fetch(
			getState().fetchDefault.baseAPI + 'api/WardrobeItem',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(newWardrobeItem),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((newWardrobeItem: IWardrobeItemEdit) => {
						const wi: IWardrobeItem = newWardrobeItem;
						wi.categoryKey = newWardrobeItem.attributes.CategoryKey;
						wi.colorKey = newWardrobeItem.attributes.ColorKey;
						wi.materialKey = newWardrobeItem.attributes.MaterialKey;
						wi.printKey = newWardrobeItem.attributes.PrintKey;
						wi.sizeKey = newWardrobeItem.attributes.SizeKey;
						dispatch(getWardrobeItemSuccess(wi));
						dispatch(closeModal());
						dispatch(getWardrobeItemsSearchResults(true, false, SearchUsage.WardrobeItemsUserCard));
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function submitUpdateWardrobeItem(item: IWardrobeItem) {
	return (dispatch, getState: () => IStore) => {
		item.metatagTitle = item.name;
		item.metatagKeywords = item.name;
		item.metatagDescription = item.description;

		dispatch(requestStart());

		const wardrobeItemEdit: IWardrobeItemEdit = item;
		wardrobeItemEdit.attributes.CategoryKey = item.categoryKey;
		wardrobeItemEdit.attributes.ColorKey = item.colorKey;
		wardrobeItemEdit.attributes.MaterialKey = item.materialKey;
		wardrobeItemEdit.attributes.PrintKey = item.printKey;
		wardrobeItemEdit.attributes.SizeKey = item.sizeKey;

		return fetch(
			getState().fetchDefault.baseAPI + 'api/WardrobeItem/' + item.id,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: JSON.stringify(wardrobeItemEdit),
			}),
		)
			.then((res) => {
				if (res.ok) {
					dispatch(getWardrobeItemSuccess(item));
					dispatch(closeModal());
					dispatch(
						notifSend({
							message: i18n.t('Notification_Success_Wardrobe_Item_Updated'),
							kind: 'success',
							dismissAfter: 3000,
						}),
					);
					dispatch(getWardrobeItemsSearchResults(true, true, SearchUsage.WardrobeItemsUserCard));
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function setCategoryDropdownsValues() {
	return (dispatch, getState: () => IStore) => {
		const categoryDomainValues: ICategoryDomainValue[] = getState().domainValues.categories;
		const wardrobeItem: IWardrobeItem = getState().manageWardrobeItem.wardrobeItem;

		const firstParentCategoryKey: number = categoryDomainValues.find(
			(x) => x.key === wardrobeItem.categoryKey,
		).parentKey;

		if (firstParentCategoryKey) {
			const secondParentCategoryKey: number = categoryDomainValues.find(
				(x) => x.key === firstParentCategoryKey,
			).parentKey;
			if (secondParentCategoryKey) {
				const thirdParentCategoryKey: number = categoryDomainValues.find(
					(x) => x.key === secondParentCategoryKey,
				).parentKey;
				if (thirdParentCategoryKey) {
					wardrobeItem.fourthDropdownCategoryKey = wardrobeItem.categoryKey;
					wardrobeItem.thirdDropdownCategoryKey = firstParentCategoryKey;
					wardrobeItem.secondDropdownCategoryKey = secondParentCategoryKey;
					wardrobeItem.firstDropdownCategoryKey = thirdParentCategoryKey;
				} else {
					wardrobeItem.thirdDropdownCategoryKey = wardrobeItem.categoryKey;
					wardrobeItem.secondDropdownCategoryKey = firstParentCategoryKey;
					wardrobeItem.firstDropdownCategoryKey = secondParentCategoryKey;
				}
			} else {
				wardrobeItem.secondDropdownCategoryKey = wardrobeItem.categoryKey;
				wardrobeItem.firstDropdownCategoryKey = firstParentCategoryKey;
			}
		} else {
			wardrobeItem.firstDropdownCategoryKey = wardrobeItem.categoryKey;
		}

		dispatch(getWardrobeItemSuccess(wardrobeItem));
	};
}

/** Action Creator  */
export function cropAndSaveWardrobeItemImage(image: ICropImageObject, formName: string) {
	return (dispatch, getState: () => IStore) => {
		dispatch(requestStart());

		return fetch(
			getState().fetchDefault.baseAPI + 'api/imageupload/crop',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(image),
			}),
		)
			.then((res) => {
				if (res.ok) {
					return res.json().then((image: IImageData) => {
						dispatch(change(formName, 'images', [{ id: image.id, href: image.href }]));
						dispatch(closeModal());
					});
				} else {
					const status: number = res.status;
					const statusText: string = res.statusText;
					return res
						.json()
						.then((res) => {
							res.status = status;
							res.statusText = statusText;
							dispatch(requestFailure(res));
						})
						.catch(() => {
							const message: any = { status, statusText };
							dispatch(requestFailure(message));
						});
				}
			})
			.catch((err) => {
				dispatch(requestFailure(err));
			});
	};
}

/** Action Creator */
export function requestStart(): IManageWardrobeItemAction {
	return {
		type: REQUEST_START,
	};
}

/** Action Creator */
export function requestFailure(message: any): IManageWardrobeItemAction {
	return {
		type: REQUEST_FAILURE,
		payload: {
			message,
		},
	};
}

/** Action Creator */
export function getWardrobeItemSuccess(wardrobeItem: IWardrobeItem): IManageWardrobeItemAction {
	return {
		type: GET_WARDROBE_ITEM_SUCCESS,
		payload: {
			wardrobeItem,
		},
	};
}

/** Action Creator */
export function clearManageWardrobeItemFormData(): IManageWardrobeItemAction {
	return {
		type: CLEAR_FORM_DATA,
	};
}

/** Action Creator */
export function clearWardrobeItemFormError(): IManageWardrobeItemAction {
	return {
		type: CLEAR_FORM_ERROR,
	};
}

/** Action Creator */
export function setIsEditMode(isEditMode: boolean): IManageWardrobeItemAction {
	return {
		type: SET_IS_EDIT_MODE,
		payload: {
			isEditMode,
		},
	};
}
